import { useEffect, useRef, useState } from 'react';
import { shakeDetectionService } from '../services/shakeDetectionService';

/**
 * Custom hook for detecting shake gestures using DeviceMotion API
 *
 * @param {boolean} enabled - Whether shake detection is active
 * @param {function} onShake - Callback function to execute when shake is detected
 * @param {number} threshold - Acceleration threshold for shake detection (default: 15)
 * @returns {object} - { isSupported, permissionGranted, requestPermission }
 */
export const useShakeDetection = (enabled, onShake, threshold = 15) => {
  const [isSupported, setIsSupported] = useState(false);
  const [permissionGranted, setPermissionGranted] = useState(false);
  const lastShakeTime = useRef(0);
  const lastX = useRef(0);
  const lastY = useRef(0);
  const lastZ = useRef(0);
  const testTimeoutRef = useRef(null); // Track test listener timeout for cleanup

  // Check if DeviceMotion is supported
  useEffect(() => {
    console.log('=== Shake Detection Feature Check ===');
    console.log('window exists:', typeof window !== 'undefined');
    console.log('DeviceMotionEvent exists:', typeof window !== 'undefined' && 'DeviceMotionEvent' in window);

    if (typeof window !== 'undefined' && 'DeviceMotionEvent' in window) {
      console.log('DeviceMotionEvent found - setting isSupported to true');
      setIsSupported(true);

      // Check if permission API exists (iOS 13+)
      const hasPermissionAPI = typeof DeviceMotionEvent.requestPermission === 'function';
      console.log('DeviceMotionEvent.requestPermission exists:', hasPermissionAPI);

      // Check if permission is already granted (non-iOS or already permitted)
      if (!hasPermissionAPI) {
        console.log('No permission API - assuming granted (Android or older iOS)');
        setPermissionGranted(true);
      } else {
        console.log('Permission API exists (iOS 13+) - checking if already granted');
        // On iOS, try to listen to device motion to detect if permission is already granted
        // If we can receive events, permission was granted previously
        const testListener = (event) => {
          if (event.accelerationIncludingGravity && event.accelerationIncludingGravity.x !== null) {
            console.log('DeviceMotion events received - permission already granted!');
            setPermissionGranted(true);
            window.removeEventListener('devicemotion', testListener);
            // Clear timeout since we got events early
            if (testTimeoutRef.current) {
              clearTimeout(testTimeoutRef.current);
              testTimeoutRef.current = null;
            }
          }
        };
        window.addEventListener('devicemotion', testListener);

        // Remove test listener after 1 second if no events received
        // Track timeout in ref so it can be cleared on unmount
        testTimeoutRef.current = setTimeout(() => {
          window.removeEventListener('devicemotion', testListener);
          testTimeoutRef.current = null;
          console.log('DeviceMotion test listener removed');
        }, 1000);
      }
    } else {
      console.log('DeviceMotionEvent NOT supported on this device');
      setIsSupported(false);
    }
    console.log('=== End Feature Check ===');

    // Cleanup: clear test timeout and remove listener on unmount
    return () => {
      if (testTimeoutRef.current) {
        clearTimeout(testTimeoutRef.current);
        testTimeoutRef.current = null;
      }
    };
  }, []);

  // Request permission (iOS 13+ only)
  const requestPermission = async () => {
    if (typeof DeviceMotionEvent.requestPermission === 'function') {
      try {
        const response = await DeviceMotionEvent.requestPermission();
        if (response === 'granted') {
          setPermissionGranted(true);
          return true;
        }
        return false;
      } catch (error) {
        console.error('Error requesting device motion permission:', error);
        return false;
      }
    }
    // Already granted (Android or older iOS)
    return true;
  };

  // Shake detection logic
  useEffect(() => {
    console.log('Shake detection effect:', { enabled, isSupported, permissionGranted });

    if (!enabled || !isSupported || !permissionGranted) {
      console.log('Shake detection not active');
      // Reset refs when disabled to prevent false positives on re-enable
      lastX.current = 0;
      lastY.current = 0;
      lastZ.current = 0;
      return;
    }

    console.log('Setting up shake detection listener - resetting motion baseline');

    // IMPORTANT: Reset refs every time we set up the listener
    // This prevents stale values from previous page visits from causing false triggers
    lastX.current = 0;
    lastY.current = 0;
    lastZ.current = 0;

    let eventCount = 0;
    let isInitialized = false;

    const handleMotion = (event) => {
      eventCount++;
      if (eventCount % 20 === 0) {
        console.log('Motion events received:', eventCount);
      }

      const current = event.accelerationIncludingGravity;

      if (!current || current.x === null) {
        if (eventCount === 1) {
          console.log('No acceleration data available');
        }
        return;
      }

      // Initialize baseline values on first event - don't compare yet
      if (!isInitialized) {
        console.log('Initializing baseline motion values');
        lastX.current = current.x;
        lastY.current = current.y;
        lastZ.current = current.z;
        isInitialized = true;
        return; // Skip detection on first event
      }

      const currentTime = Date.now();

      // Debounce: prevent multiple triggers within 1 second
      if (currentTime - lastShakeTime.current < 1000) {
        return;
      }

      // Calculate change in acceleration
      const deltaX = Math.abs(current.x - lastX.current);
      const deltaY = Math.abs(current.y - lastY.current);
      const deltaZ = Math.abs(current.z - lastZ.current);

      const maxDelta = Math.max(deltaX, deltaY, deltaZ);

      if (maxDelta > 5) {
        console.log('Motion detected:', { deltaX, deltaY, deltaZ, threshold });
      }

      // Check if shake threshold exceeded
      if (deltaX > threshold || deltaY > threshold || deltaZ > threshold) {
        console.log('SHAKE DETECTED!', { deltaX, deltaY, deltaZ });

        // Check global cooldown before triggering
        if (!shakeDetectionService.canShakeNow()) {
          console.log('Shake ignored due to global cooldown');
          return;
        }

        lastShakeTime.current = currentTime;

        // Update global shake timestamp
        shakeDetectionService.setLastShakeTime();

        // Trigger haptic feedback if available
        if (navigator.vibrate) {
          navigator.vibrate(200);
        }

        // Execute callback
        onShake();
      }

      // Store current values for next comparison
      lastX.current = current.x;
      lastY.current = current.y;
      lastZ.current = current.z;
    };

    window.addEventListener('devicemotion', handleMotion);

    return () => {
      console.log('Removing shake detection listener');
      window.removeEventListener('devicemotion', handleMotion);
    };
  }, [enabled, isSupported, permissionGranted, onShake, threshold]);

  return {
    isSupported,
    permissionGranted,
    requestPermission,
  };
};
